import React, { useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import "../../UI/map.css";

const MAPLIBRE_JS = "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.js";
const MAPLIBRE_CSS = "https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css";

const buildCurvedLine = (start, end, segments = 48) => {
  if (!start || !end) return null;
  const [lng1, lat1] = start;
  const [lng2, lat2] = end;
  const dx = lng2 - lng1;
  const dy = lat2 - lat1;
  const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
  const normX = -dy / dist;
  const normY = dx / dist;
  const offset = dist * 0.3; // adjust curvature strength
  const cx = (lng1 + lng2) / 2 + normX * offset;
  const cy = (lat1 + lat2) / 2 + normY * offset;

  const coords = [];
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    // quadratic bezier interpolation
    const x =
      (1 - t) * (1 - t) * lng1 + 2 * (1 - t) * t * cx + t * t * lng2;
    const y =
      (1 - t) * (1 - t) * lat1 + 2 * (1 - t) * t * cy + t * t * lat2;
    coords.push([x, y]);
  }
  return coords;
};

const loadMaplibre = () =>
  new Promise((resolve, reject) => {
    if (window.maplibregl) {
      resolve(window.maplibregl);
      return;
    }
    const existingScript = document.querySelector("script[data-maplibre]");
    if (existingScript) {
      existingScript.addEventListener("load", () =>
        resolve(window.maplibregl || window.maplibregl),
      );
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = MAPLIBRE_CSS;
    link.setAttribute("data-maplibre", "css");
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.setAttribute("data-maplibre", "script");
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const MapPreviewInline = ({
  deliveryCoords,
  pharmacyCoords,
  pharmacies = [],
  selectedPharmacyId = null,
  height = "200px",
  borderRadius = "10px",
  editable = false,
  activePin = "delivery",
  onSelectDelivery,
  onSelectPharmacy,
  showRoute = true,
}) => {
  const pinBase = `${process.env.PUBLIC_URL || ""}/icons`;
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const maplibreRef = useRef(null);
  const deliveryMarkerRef = useRef(null);
  const pharmacyMarkerRef = useRef(null);
  const extraPharmacyMarkersRef = useRef([]);
  const routeAddedRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);

  // initialize map once
  useEffect(() => {
    let mapInstance;
    loadMaplibre()
      .then((maplibre) => {
        maplibreRef.current = maplibre;
        mapInstance = new maplibre.Map({
          container: containerRef.current,
          style: {
            version: 8,
            sources: {
              osm: {
                type: "raster",
                tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
                tileSize: 256,
                maxzoom: 19,
              },
            },
            layers: [
              {
                id: "osm",
                type: "raster",
                source: "osm",
              },
            ],
          },
          center: [77.209, 28.6139],
          zoom: 12,
          attributionControl: false,
        });
        if (!editable) {
          mapInstance.scrollZoom.disable();
          mapInstance.boxZoom.disable();
          mapInstance.doubleClickZoom.disable();
          mapInstance.touchZoomRotate.disableRotation();
        }
        mapInstance.on("load", () => {
          mapRef.current = mapInstance;
          setMapReady(true);
          requestAnimationFrame(() => mapInstance.resize());
        });
      })
      .catch(() => {});

    return () => {
      if (mapInstance && typeof mapInstance.remove === "function") {
        mapInstance.remove();
      }
    };
  }, []);

  // update markers/route when coords change
  useEffect(() => {
    const normalize = (coords) => {
      if (!Array.isArray(coords) || coords.length !== 2) return null;
      const lng = Number(coords[0]);
      const lat = Number(coords[1]);
      if (Number.isNaN(lng) || Number.isNaN(lat)) return null;
      return [lng, lat];
    };
    const delivery = normalize(deliveryCoords);
    const pharmacy = normalize(pharmacyCoords);
    const map = mapRef.current;
    const maplibre = maplibreRef.current;
    const hasDelivery = Boolean(delivery);
    const hasPharmacy = Boolean(pharmacy);
    if (!map || !maplibre || !mapReady) return;

    if (!hasDelivery && !hasPharmacy) {
      map.setCenter([77.209, 28.6139]);
      map.setZoom(12);
      return;
    }

    // clear previous markers
    [deliveryMarkerRef, pharmacyMarkerRef].forEach((ref) => {
      if (ref.current) {
        ref.current.remove();
        ref.current = null;
      }
    });
    extraPharmacyMarkersRef.current.forEach((marker) => marker.remove());
    extraPharmacyMarkersRef.current = [];

    const makePin = (src, alt, size = { w: 36, h: 48 }) => {
      const el = document.createElement("img");
      el.src = src;
      el.alt = alt;
      el.className = "map-pharmacy-pin";
      el.style.width = `${size.w}px`;
      el.style.height = `${size.h}px`;
      el.style.objectFit = "contain";
      return el;
    };

    if (hasDelivery) {
      const el = makePin(`${pinBase}/pin-user.png`, "Delivery");
      deliveryMarkerRef.current = new maplibre.Marker({ element: el, anchor: "bottom" })
        .setLngLat(delivery)
        .addTo(map);
    }
    if (hasPharmacy) {
      const el = makePin(`${pinBase}/pin-pharmacy.png`, "Pharmacy");
      pharmacyMarkerRef.current = new maplibre.Marker({ element: el, anchor: "bottom" })
        .setLngLat(pharmacy)
        .addTo(map);
    }

    if (Array.isArray(pharmacies) && pharmacies.length) {
      pharmacies.forEach((entry) => {
        const coords = normalize(entry?.coords || entry?.location?.coordinates);
        if (!coords) return;
        const isSelected = String(entry?.id || entry?.pharmacyId || "") === String(selectedPharmacyId || "");
        if (hasPharmacy && isSelected) return;

        const el = makePin(`${pinBase}/pin-pharmacy.png`, entry?.name || "Pharmacy", isSelected ? { w: 38, h: 50 } : { w: 32, h: 42 });
        el.style.opacity = isSelected ? "1" : "0.9";
        const marker = new maplibre.Marker({ element: el, anchor: "bottom" })
          .setLngLat(coords)
          .setPopup(new maplibre.Popup({ offset: 18 }).setText(entry?.name || "Pharmacy"))
          .addTo(map);
        extraPharmacyMarkersRef.current.push(marker);
      });
    }

    // route layer
    const sourceId = "preview-route";
    const layerId = "preview-route-layer";
    const existingSource = typeof map.getSource === "function" ? map.getSource(sourceId) : null;
    if (!existingSource) {
      map.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }
    if (!map.getLayer || !map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": "#f97316",
          "line-width": 4,
          "line-dasharray": [2, 1.2],
          "line-opacity": 0.85,
        },
      });
    }
    const routeSource = typeof map.getSource === "function" ? map.getSource(sourceId) : null;
    if (hasDelivery && hasPharmacy && routeSource?.setData && showRoute) {
      const curveCoords = buildCurvedLine(delivery, pharmacy) || [
        delivery,
        pharmacy,
      ];
      routeSource.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: curveCoords,
            },
          },
        ],
      });
      routeAddedRef.current = true;
    } else if (routeAddedRef.current && routeSource?.setData) {
      routeSource.setData({
        type: "FeatureCollection",
        features: [],
      });
      routeAddedRef.current = false;
    }

    // fit bounds
    const listCoords = Array.isArray(pharmacies)
      ? pharmacies
          .map((entry) => normalize(entry?.coords || entry?.location?.coordinates))
          .filter(Boolean)
      : [];
    const pts = [delivery, pharmacy, ...listCoords].filter(Boolean);
    if (pts.length === 1) {
      map.setCenter(pts[0]);
      map.setZoom(13.5);
    } else if (pts.length === 2) {
      const bounds = new maplibre.LngLatBounds(pts[0], pts[0]).extend(pts[1]);
      map.fitBounds(bounds, { padding: { top: 40, bottom: 40, left: 50, right: 50 }, maxZoom: 14, duration: 400 });
    }
  }, [deliveryCoords, pharmacyCoords, pharmacies, selectedPharmacyId, mapReady, pinBase, showRoute]);

  useEffect(() => {
    if (!mapReady || !containerRef.current || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(() => {
      if (mapRef.current?.resize) mapRef.current.resize();
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [mapReady]);

  useEffect(() => {
    if (!editable || !mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const handleClick = (e) => {
      const lngLat = [e.lngLat.lng, e.lngLat.lat];
      if (activePin === "delivery") {
        onSelectDelivery?.(lngLat);
      } else if (activePin === "pharmacy") {
        onSelectPharmacy?.(lngLat);
      }
    };
    map.on("click", handleClick);
    return () => {
      map.off("click", handleClick);
    };
  }, [editable, activePin, mapReady, onSelectDelivery, onSelectPharmacy]);

  return (
    <Box
      ref={containerRef}
      h={height}
      borderRadius={borderRadius}
      overflow="hidden"
      border="1px solid #cbd5e1"
      position="relative"
      boxShadow="0 10px 24px rgba(15,23,42,0.12)"
    />
  );
};

export default MapPreviewInline;
